import { useState, useMemo, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, X, Search, ChevronDown, ChevronUp, Users, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { formatPhoneForWhatsApp } from "@/utils/phone";
import { formatGroupTime } from "@/lib/eventGroups";
import { compareGuestNames } from "@/lib/guestSort";

type AttendanceStatus = "present" | "no_show" | null;

interface TicketRow {
  id: string;
  quantity: number;
  user_id: string;
  attendance_status: string | null;
  created_at: string;
  group_id: string | null;
  marked_by: string | null;
  marked_at: string | null;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    phone: string | null;
  } | null;
  marker: { first_name: string | null } | null;
}

interface Props {
  eventId: string;
  tickets: TicketRow[];
  capacity: number;
  eventCounts: Record<string, number>;
  groups?: Array<{ id: string; name: string; start_time: string }>;
  timezone?: string;
  /** Preselects the wave a host runs, so they land on their own group. */
  defaultGroupId?: string | null;
}

const PAGE_SIZE = 20;

export function EventCheckinManager({ eventId, tickets: allTickets, capacity, eventCounts, groups = [], timezone, defaultGroupId = null }: Props) {
  const queryClient = useQueryClient();
  const [groupFilter, setGroupFilter] = useState<string | null>(defaultGroupId);
  const tickets = useMemo(
    () => (groupFilter ? allTickets.filter((t) => t.group_id === groupFilter) : allTickets),
    [allTickets, groupFilter]
  );
  const guestsIn = (list: TicketRow[]) => list.reduce((s, t) => s + (t.quantity || 1), 0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Only this host's unsaved edits live here. A refetch bringing in another
  // host's marks must never count as "changes" — otherwise auto-save would
  // send stale nulls back and wipe their work.
  const [pending, setPending] = useState<Record<string, AttendanceStatus>>({});

  const normalise = (s: string | null): AttendanceStatus =>
    s === "present" || s === "no_show" ? s : null;

  const statusFor = (t: TicketRow): AttendanceStatus =>
    t.id in pending ? pending[t.id] : normalise(t.attendance_status);

  // Filtered tickets, ordered A→Z by first name so the list reads like a roll call
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? tickets.filter((t) => {
          const name = `${t.profiles?.first_name || ""} ${t.profiles?.last_name || ""}`.toLowerCase();
          return name.includes(q);
        })
      : tickets;
    return [...matched].sort((a, b) => compareGuestNames(a.profiles, b.profiles));
  }, [tickets, search]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Stats (read through statusFor so they include other hosts' saved marks)
  const presentCount = tickets.filter((t) => statusFor(t) === "present").length;
  const noShowCount = tickets.filter((t) => statusFor(t) === "no_show").length;
  const unmarkedCount = tickets.length - presentCount - noShowCount;
  const totalGuests = tickets.reduce((sum, t) => sum + (t.quantity || 1), 0);

  // Unsaved changes = only what this host has tapped since the last save
  const hasChanges = Object.keys(pending).length > 0;

  // Save mutation — sends only this host's pending edits, then clears exactly
  // the keys it sent (a tap made while the save is in flight stays pending).
  const save = useMutation({
    mutationFn: async (records: Array<{ ticketId: string; status: AttendanceStatus }>) => {
      if (records.length === 0) return;

      const { data, error } = await supabase.functions.invoke("mark-event-attendance", {
        body: { eventId, attendanceRecords: records },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    // Return the refetch so the mutation stays "pending" until saved values are
    // back — otherwise auto-save would see stale originals and save twice.
    onSuccess: (_data, records) => {
      setPending((prev) => {
        const next = { ...prev };
        records.forEach(({ ticketId, status }) => {
          // Only clear when what's pending still equals what was just saved — a
          // re-tap during the request stays pending for the next auto-save.
          if (next[ticketId] === status) delete next[ticketId];
        });
        return next;
      });
      return queryClient.invalidateQueries({ queryKey: ["checkin-tickets", eventId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to save attendance");
    },
  });

  const saveNow = () => {
    const records = Object.entries(pending).map(([ticketId, status]) => ({ ticketId, status }));
    if (records.length > 0) save.mutate(records);
  };

  // Auto-save shortly after the host stops tapping. After a failure we wait for
  // the next tap or the manual button rather than retrying in a loop.
  useEffect(() => {
    if (!hasChanges || save.isPending || save.isError) return;
    const t = setTimeout(saveNow, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, hasChanges, save.isPending, save.isError]);

  // Warn before leaving with unsaved marks.
  useEffect(() => {
    if (!hasChanges) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasChanges]);

  const toggleStatus = (ticketId: string, status: AttendanceStatus) => {
    if (save.isError) save.reset();
    setPending((prev) => {
      const ticket = allTickets.find((t) => t.id === ticketId);
      const current = ticketId in prev ? prev[ticketId] : normalise(ticket?.attendance_status ?? null);
      const next = current === status ? null : status;
      // Tapping back to the server's value cancels the edit instead of
      // queuing a redundant write.
      if (next === normalise(ticket?.attendance_status ?? null)) {
        const { [ticketId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketId]: next };
    });
  };

  const getName = (t: TicketRow) => {
    if (!t.profiles) return "Unknown";
    return `${t.profiles.first_name || ""} ${t.profiles.last_name || ""}`.trim() || "Unknown";
  };

  const getInitials = (t: TicketRow) => {
    if (!t.profiles) return "?";
    return ((t.profiles.first_name?.[0] || "") + (t.profiles.last_name?.[0] || "")).toUpperCase() || "?";
  };

  const presentTickets = tickets.filter((t) => statusFor(t) === "present");

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
        <Users className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex items-center gap-3 text-sm flex-wrap">
          <span className="font-medium text-foreground">{totalGuests} guests</span>
          <span className="text-emerald-600 font-medium">{presentCount} present</span>
          {noShowCount > 0 && (
            <span className="text-destructive font-medium">{noShowCount} no-show</span>
          )}
          <span className="text-muted-foreground">{unmarkedCount} remaining</span>
        </div>
      </div>

      {/* Wave filter */}
      {groups.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[{ id: null as string | null, label: "All", count: guestsIn(allTickets) },
            ...groups.map((g) => ({
              id: g.id as string | null,
              label: `${g.name} · ${formatGroupTime(g.start_time, timezone)}`,
              count: guestsIn(allTickets.filter((t) => t.group_id === g.id)),
            }))].map((tab) => (
            <Button
              key={tab.id ?? "all"}
              type="button"
              size="sm"
              variant={groupFilter === tab.id ? "default" : "outline"}
              className="shrink-0 h-9"
              onClick={() => { setGroupFilter(tab.id); setPage(0); }}
            >
              {tab.label} ({tab.count})
            </Button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="pl-10 h-11"
        />
      </div>

      {/* Guest list */}
      <div className="space-y-2">
        {paged.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {search ? "No guests match your search" : "No confirmed guests"}
          </p>
        ) : (
          paged.map((ticket) => {
            const current = statusFor(ticket);
            const pastEvents = eventCounts[ticket.user_id] || 0;
            const isFirstTimer = pastEvents <= 1; // 1 = this event only
            const phone = ticket.profiles?.phone;

            return (
              <div
                key={ticket.id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                  current === "present" && "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-900",
                  current === "no_show" && "border-destructive/20 bg-destructive/5",
                  current === null && "bg-card"
                )}
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={ticket.profiles?.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">{getInitials(ticket)}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{getName(ticket)}</p>
                    {isFirstTimer && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                        First Timer
                      </Badge>
                    )}
                    {ticket.quantity > 1 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                        +{ticket.quantity - 1}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {phone && (
                      <a
                        href={`https://wa.me/${formatPhoneForWhatsApp(phone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Phone className="h-3 w-3" />
                        <span className="truncate">{phone}</span>
                      </a>
                    )}
                    {!isFirstTimer && (
                      <span className="text-xs text-muted-foreground">{pastEvents} events</span>
                    )}
                    {current !== null && ticket.marker?.first_name && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        ✓ {ticket.marker.first_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Attendance buttons */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-11 w-11 rounded-lg",
                      current === "present"
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900 dark:text-emerald-300"
                        : "hover:bg-emerald-50 hover:text-emerald-600"
                    )}
                    onClick={() => toggleStatus(ticket.id, "present")}
                  >
                    <Check className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-11 w-11 rounded-lg",
                      current === "no_show"
                        ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                        : "hover:bg-destructive/5 hover:text-destructive"
                    )}
                    onClick={() => toggleStatus(ticket.id, "no_show")}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Collapsible summary */}
      {presentTickets.length > 0 && (
        <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between text-sm h-10">
              <span>Marked Present ({presentTickets.length})</span>
              {summaryOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-wrap gap-1.5 p-3 rounded-lg bg-muted/50">
              {presentTickets.map((t) => (
                <Badge key={t.id} variant="secondary" className="text-xs">
                  {getName(t)}
                </Badge>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Sticky save bar — sits above the bottom navigation (z-50, ~4rem tall) */}
      <div className="fixed bottom-16 left-0 right-0 px-4 py-3 bg-background/95 backdrop-blur border-t border-border shadow-lg z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <p
            className={cn(
              "flex-1 text-sm font-medium",
              save.isError ? "text-destructive" : hasChanges || save.isPending ? "text-muted-foreground" : "text-foreground"
            )}
            aria-live="polite"
          >
            {save.isPending
              ? "Saving…"
              : save.isError
                ? "Not saved — tap Save to retry"
                : hasChanges
                  ? "Unsaved changes…"
                  : "✓ All changes saved"}
          </p>
          <Button
            onClick={saveNow}
            disabled={save.isPending || !hasChanges}
            className="h-12 px-6 text-base shrink-0"
          >
            {save.isPending ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Saving
              </>
            ) : (
              "Save now"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
